import { gqlFetch } from '@/service/gql'
import { gql, imageFieldsFrag } from '@amzn/amp-playback'
import { type SanitizedResult } from '@amzn/amp-gql'
import { getAuth } from '@/auth/authRef'

// Enrolled household profiles plus the per-profile permission triple GH's
// "who is listening" picker gates on. Read through `account.groups` (not the
// top-level `profiles`) so `permissions` resolves in the household context —
// mirrors GH's `profileGroup` query.
const selectableProfilesQuery = gql(
    `
    query selectableProfiles {
        account {
            groups {
                edges {
                    node {
                        profiles(statusFilter: "ENROLLED") {
                            edges {
                                permissions {
                                    assumable { isEligible }
                                    editable { isEligible }
                                    invitable { isEligible }
                                }
                                node {
                                    id
                                    name
                                    profileType
                                    images {
                                        ...imageFields
                                    }
                                    user {
                                        id
                                        handle
                                        images {
                                            ...imageFields
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    `,
    [imageFieldsFrag]
)

export type SelectableProfilesResult = SanitizedResult<typeof selectableProfilesQuery>

type GroupEdges = NonNullable<
    NonNullable<NonNullable<SelectableProfilesResult['account']>['groups']>['edges']
>
type GroupNode = NonNullable<NonNullable<GroupEdges[number]>['node']>
type ProfileEdges = NonNullable<NonNullable<GroupNode['profiles']>['edges']>
type ProfileEdge = NonNullable<ProfileEdges[number]>
export type SelectProfileNode = NonNullable<ProfileEdge['node']>

export interface SelectableProfile {
    readonly node: SelectProfileNode
    /** GH's `isAvailable`: assumable OR editable OR invitable is eligible. */
    readonly available: boolean
}

// GH: a profile is pickable when any of assume/edit/invite is eligible;
// otherwise it is a credentialed profile that must sign in separately.
function isEdgeAvailable(edge: ProfileEdge): boolean {
    const p = edge.permissions
    return (
        p?.assumable?.isEligible === true ||
        p?.editable?.isEligible === true ||
        p?.invitable?.isEligible === true
    )
}

export async function fetchSelectableProfiles(): Promise<readonly SelectableProfile[]> {
    const res = await gqlFetch(selectableProfilesQuery, {})
    const edges = res.account?.groups?.edges[0]?.node?.profiles?.edges ?? []
    const out: SelectableProfile[] = []
    for (const edge of edges) {
        if (!edge.node.id) continue
        out.push({ node: edge.node, available: isEdgeAvailable(edge) })
    }
    return out
}

/** Whether the active profile denotes a child profile — filtered from the grid. */
export function isChildNode(node: SelectProfileNode): boolean {
    return node.profileType === 'CHILD'
}

const pendingProfileTransferQuery = gql(
    `
    query PendingProfileTransfer {
        profile {
            id
        }
        account {
            groups {
                edges {
                    node {
                        profiles {
                            edges {
                                role
                                permissions {
                                    transferable {
                                        isEligible
                                        reason
                                    }
                                }
                                node {
                                    id
                                    name
                                    user {
                                        id
                                        name
                                        images {
                                            ...imageFields
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    `,
    [imageFieldsFrag]
)

type TransferGroupEdges = NonNullable<
    NonNullable<
        NonNullable<SanitizedResult<typeof pendingProfileTransferQuery>['account']>['groups']
    >['edges']
>
type TransferProfileEdges = NonNullable<
    NonNullable<NonNullable<TransferGroupEdges[number]>['node']>['profiles']
>['edges']
type TransferProfileEdge = NonNullable<NonNullable<TransferProfileEdges>[number]>
export type TransferProfileNode = NonNullable<TransferProfileEdge['node']>

export type ProfileTransferMessage = 'PENDING_TARGET' | 'PENDING_SOURCE' | 'DECLINED_SOURCE'

export interface PendingProfileTransfer {
    readonly message: ProfileTransferMessage
    readonly profileId: string
    readonly profile: TransferProfileNode
    /** The group owner, named on the source side. */
    readonly owner: TransferProfileNode | null
}

const TRANSITION_PENDING_REASON = 'TRANSFER_BLOCKED_PROFILE_TRANSITION_PENDING'
const TRANSITION_DECLINED_REASON = 'TRANSFER_BLOCKED_PROFILE_NOT_AUTHORIZED'

// Firefly's `Role`: OWNER and LINKED are an account's default profile (the transfer target),
// MEMBER a secondary profile (the source); any other role gets no message.
function transferMessageOf(edge: TransferProfileEdge): ProfileTransferMessage | null {
    const { isEligible, reason } = edge.permissions?.transferable ?? {}
    if (isEligible !== false) return null
    const isTarget = edge.role === 'OWNER' || edge.role === 'LINKED'
    if (!isTarget && edge.role !== 'MEMBER') return null
    if (reason === TRANSITION_PENDING_REASON) return isTarget ? 'PENDING_TARGET' : 'PENDING_SOURCE'
    if (reason === TRANSITION_DECLINED_REASON && !isTarget) return 'DECLINED_SOURCE'
    return null
}

/**
 * The active profile's blocked P2A transfer, if any. Mirrors GH's
 * PendingProfileTransfer check: only the active profile's own group counts, so a
 * sibling's transfer never raises it, and the read bypasses the gateway
 * api-cache so the transfer state is live.
 */
export async function fetchPendingProfileTransfer(): Promise<PendingProfileTransfer | null> {
    const res = await gqlFetch(pendingProfileTransferQuery, {}, { featureFlags: 'use-ups' })
    const activeProfileId = res.profile?.id
    if (!activeProfileId) return null
    const isActive = (edge: TransferProfileEdge) => edge.node.id === activeProfileId
    const activeGroup = (res.account?.groups?.edges ?? [])
        .map((group) => group.node.profiles?.edges ?? [])
        .find((edges) => edges.some(isActive))
    const active = activeGroup?.find(isActive)
    const message = active ? transferMessageOf(active) : null
    if (!active || !message) return null
    const owner = activeGroup?.find((edge) => edge.role === 'OWNER')?.node ?? null
    return { message, profileId: activeProfileId, profile: active.node, owner }
}

const SOCIAL_DEFAULT_NAME = 'amazon music user'

/** GH's `getProfileName`: the social name, unless it is still the default, else the profile name. */
export function transferProfileName(node: TransferProfileNode): string {
    const socialName = node.user?.name ?? ''
    if (socialName && socialName.toLowerCase() !== SOCIAL_DEFAULT_NAME) return socialName
    return node.name ?? ''
}

/**
 * Profile switching re-mints the session under the picked profile. Only the
 * cookie-session (AuthPortal) controller can do it — the swap mutates the
 * first-party session cookie server-side. Bearer controllers (LWA/MAP/CBL)
 * cannot, matching GH's web-only switch (its MAP store is a no-op).
 */
export function canSwitchProfile(): boolean {
    return !getAuth().usesBearer()
}

interface UpdateProfileResponse {
    readonly success?: boolean
}

// Same-origin AuthPortal endpoint GH's web build hits; the server swaps the
// session cookie/token for the profile and answers `{ success }`.
function updateProfileEndpoint(profileId: string): string {
    return `/updateProfile?profileId=${encodeURIComponent(profileId)}`
}

// Only same-origin absolute paths survive as a redirect target; anything else
// (protocol-relative `//host`, absolute URL, empty) falls back to home.
function safeReturnPath(returnTo: string | undefined): string {
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) return returnTo
    return '/'
}

function reloadTo(path: string): void {
    location.assign(path)
}

/**
 * Switch the active listener profile. Swaps the session cookie via the
 * AuthPortal endpoint, then does a full reload so `/config.json` is re-read
 * under the new cookie — re-minting the token and re-initializing every
 * per-session memo (customer / userContext / childProfile / changeSubscription)
 * for the new profile. This is the SPA-reload equivalent of GH's
 * `refetchQueries` + `resetIdentity`, and avoids inheriting the previous
 * profile's cached age band / tier.
 */
export async function switchProfile(profileId: string, returnTo?: string): Promise<void> {
    if (!canSwitchProfile()) {
        throw new Error('profile switch requires a cookie-session (AuthPortal) sign-in')
    }
    const response = await fetch(updateProfileEndpoint(profileId), { credentials: 'include' })
    if (!response.ok) {
        throw new Error(`profile switch failed: HTTP ${response.status}`)
    }
    const body: UpdateProfileResponse = await response.json()
    if (!body.success) {
        throw new Error('profile switch was not acknowledged')
    }
    reloadTo(safeReturnPath(returnTo))
}
